import { useCallback, useEffect, useState } from 'react';
import { BookOpenCheck, Check, Download } from 'lucide-react';
import { api, ErrorApi } from '../api';
import { guardarArchivo } from '../ui/descarga';
import { Cargando } from '../ui/EstadoDePantalla';

// «TENGO QUE LEER» · Fase 2 del módulo de Calidad.
//
// ES LA PANTALLA QUE DECIDE SI ESTO SE USA. Las otras dos —la lista de
// documentos y la matriz— las mira calidad de vez en cuando. Esta la mira
// un operador, y si no es de un vistazo y de un toque, nadie acusa nada y
// la matriz se queda en rojo para siempre.
//
// POR ESO:
//   · Cada renglón tiene DOS acciones y no más: leerlo y decir que lo
//     leíste. En ese orden, y la segunda deshabilitada hasta descargar
//     no — porque alguien puede haberlo leído impreso, y bloquearlo
//     obligaría a mentir descargándolo.
//   · Los objetivos táctiles son grandes: esto se usa en el andén, de
//     pie y con una mano (Doctrina 2, regla 9).
//   · Cuando no hay nada, lo dice y explica cuándo aparecerá algo.

export default function MisLecturas() {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [ocupado, setOcupado] = useState(null);
  const [bajando, setBajando] = useState(null);

  const cargar = useCallback(() => {
    api.get('/documentos/mis-pendientes')
      .then(setDatos)
      .catch((e) => setError(e.message));
  }, []);

  useEffect(cargar, [cargar]);

  async function abrir(p) {
    setError(null);
    setBajando(p.id_revision);
    try {
      const blob = await api.bajar(`/documentos/revision/${p.id_revision}/archivo`);
      guardarArchivo(blob, `${p.codigo}-rev${p.numero}.pdf`);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message
        : 'No se pudo abrir el documento.');
    } finally {
      setBajando(null);
    }
  }

  async function acusar(p) {
    setError(null);
    setOcupado(p.id_revision);
    try {
      const d = await api.post(`/documentos/revision/${p.id_revision}/acuse`, {});
      setMsg(d.mensaje);
      cargar();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo registrar.');
    } finally {
      setOcupado(null);
    }
  }

  if (error && !datos) {
    return (
      <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">
        {error}
      </div>
    );
  }
  if (!datos) return <Cargando filas={4} />;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Tengo que leer</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Los documentos que te tocan y falta marcar como leídos.
        </p>
      </div>

      {msg ? (
        <p role="status" className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3">
          {msg}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
          {error}
        </p>
      ) : null}

      {datos.pendientes.length === 0 ? (
        <div className="border border-dashed border-gray-300 rounded-xl p-6 text-center">
          <Check size={28} className="mx-auto text-green-600" aria-hidden="true" />
          <p className="mt-2 font-medium text-gray-900">Estás al día</p>
          <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
            {datos.mensaje_vacio}
          </p>
        </div>
      ) : null}

      <div className="space-y-3">
        {datos.pendientes.map((p) => (
          <div key={p.id_revision}
               className="border border-amber-200 bg-amber-50/50 rounded-xl p-4">
            <div className="flex flex-wrap items-center gap-2">
              <BookOpenCheck size={18} className="text-amber-700" aria-hidden="true" />
              <span className="font-mono text-sm font-semibold text-gray-900">
                {p.codigo}
              </span>
              <span className="text-sm text-gray-900">{p.titulo}</span>
            </div>
            <p className="mt-1 text-xs text-gray-600">
              {p.tipo_nombre} · revisión {p.numero}
            </p>

            <div className="mt-3 flex flex-wrap gap-2">
              {/* OBJETIVOS GRANDES: esto se usa de pie, en el andén.
                  Y NO ES UN <a href>: la sesión viaja en la cabecera
                  Authorization, que un enlace normal no manda. Daría 401
                  y el navegador enseñaría una página en blanco sin decir
                  por qué. Se pide con la sesión puesta y se guarda. */}
              <button
                onClick={() => abrir(p)}
                disabled={bajando === p.id_revision}
                className="inline-flex items-center gap-1.5 border border-gray-300 bg-white text-gray-800 text-sm font-medium px-4 py-2.5 rounded-lg disabled:opacity-50"
              >
                <Download size={16} aria-hidden="true" />
                {bajando === p.id_revision ? 'Abriendo…' : 'Abrir el documento'}
              </button>
              <button
                onClick={() => acusar(p)}
                disabled={ocupado === p.id_revision}
                className="inline-flex items-center gap-1.5 bg-green-600 text-white text-sm font-medium px-4 py-2.5 rounded-lg disabled:opacity-50"
              >
                <Check size={16} aria-hidden="true" />
                {ocupado === p.id_revision ? 'Registrando…' : 'Ya lo leí'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
